"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.evaluateJsonata = exports.JsonataError = void 0;
const jsonata_1 = __importDefault(require("jsonata"));
const formula_1 = require("../formula");
const lruCache_1 = require("../lruCache");
// Regexes run natively, past the timeout, so every regex fails. Text patterns in $replace, $split and
// $contains match literally, so stages don't need them
const NoRegex = function () {
    throw new Error('Regular expressions are not supported');
};
// $eval would run answer text as code; a $toMillis picture builds a regex outside the regex engine
const BLOCKED_FUNCTIONS = ['eval', 'toMillis'];
// jsonata 2.2 guardrails. Evaluation blocks the event loop until the timeout, so keep it short; it is
// wall-clock, so time on concurrent evaluations counts too
const OPTIONS = { recover: false, timeout: 1000, stack: 10_000, sequence: 1_000_000, RegexEngine: NoRegex };
/** A JSONata compile or evaluation failure; jsonata itself throws plain objects, kept as the cause */
class JsonataError extends Error {
    formula;
    constructor(e, formula) {
        const message = typeof e === 'object' && e !== null && 'message' in e ? e.message : e;
        super(typeof message === 'string' ? message : 'JSONata failed', { cause: e });
        this.formula = formula;
        this.name = 'JsonataError';
    }
}
exports.JsonataError = JsonataError;
// Each evaluate call gets its own environment (only $now() is shared), so a compiled expression can be cached
const cache = new lruCache_1.LruCache(1000);
// A lambda signature becomes a native RegExp that runs outside the regex engine and the timeout. Walks
// without recursion, as a deeply nested formula could overflow the stack
const hasSignature = (ast) => {
    const nodes = [ast];
    while (nodes.length > 0) {
        const node = nodes.pop();
        if (typeof node === 'object' && node !== null) {
            if ('type' in node && node.type === 'lambda' && 'signature' in node && node.signature) {
                return true;
            }
            nodes.push(...Object.values(node));
        }
    }
    return false;
};
const compile = (formula) => {
    if (formula.length > formula_1.MAX_FORMULA_LENGTH) {
        throw new Error(`Formula is longer than ${formula_1.MAX_FORMULA_LENGTH} characters`);
    }
    let compiled = cache.get(formula);
    if (!compiled) {
        try {
            compiled = (0, jsonata_1.default)(formula, OPTIONS);
            if (hasSignature(compiled.ast())) {
                throw new Error('Function signatures are not supported');
            }
            for (const name of BLOCKED_FUNCTIONS) {
                compiled.registerFunction(name, () => {
                    throw new Error(`$${name} is not supported`);
                });
            }
            cache.set(formula, compiled);
        }
        catch (e) {
            compiled = new JsonataError(e, formula);
            // A stack overflow depends on the caller's stack, so it may compile next time
            if (!(e instanceof RangeError)) {
                cache.set(formula, compiled);
            }
        }
    }
    if (compiled instanceof JsonataError) {
        throw compiled;
    }
    return compiled;
};
/** Rejects with an Error when the expression can't be compiled or fails for this input */
const evaluateJsonata = async (formula, input, bindings) => {
    const expression = compile(formula);
    try {
        return (await expression.evaluate(input, bindings));
    }
    catch (e) {
        throw new JsonataError(e, formula);
    }
};
exports.evaluateJsonata = evaluateJsonata;
